/* SidebarLinks.js */
import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { IoMdHome } from "react-icons/io";
import { MdKeyboardArrowRight } from "react-icons/md";

export function SidebarLinks({ routes }) {
  const location = useLocation();

  const activeRoute = (routePath) =>
    location.pathname.startsWith("/" + routePath);

  const isAnyChildActive = (route) =>
    Array.isArray(route.children) &&
    route.children.some((child) => activeRoute(child.path));

  const computeOpenFromLocation = useMemo(() => {
    return () => {
      const map = {};
      routes.forEach((r) => {
        if (Array.isArray(r.children) && r.children.length > 0) {
          const parentMatch = activeRoute(r.path);
          const childMatch = r.children.some((c) => activeRoute(c.path));
          if (parentMatch || childMatch) map[r.name] = true;
        }
      });
      return map;
    };
  }, [routes, location.pathname]);

  const [openMap, setOpenMap] = useState(() => computeOpenFromLocation());

  useEffect(() => {
    setOpenMap(computeOpenFromLocation());
  }, [computeOpenFromLocation]);

  const toggleGroup = (name) =>
    setOpenMap((prev) => {
      const isOpening = !prev[name];
      return isOpening ? { [name]: true } : {};
      // Multi-open version:
      // return { ...prev, [name]: !prev[name] };
    });

  const createLinks = (routes) =>
    routes.map((route, index) => {
      if (route.hidden) return null;
      const hasChildren =
        Array.isArray(route.children) && route.children.length > 0;
      const active = activeRoute(route.path) || isAnyChildActive(route);

      // PARENT ROW
      const ParentRow = (
        <div
          className="relative mb-3 flex hover:cursor-pointer"
          key={`parent-${index}`}
        >
          <li
            className="my-[3px] flex w-full cursor-pointer items-center justify-between px-8"
            onClick={() => (hasChildren ? toggleGroup(route.name) : null)}
          >
            <div className="flex items-center">
              <span
                className={`${
                  active
                    ? "font-bold text-brand-500 dark:text-white"
                    : "font-medium text-gray-600"
                }`}
              >
                {route.icon ? route.icon : <IoMdHome />}
              </span>
              <p
                className={`leading-1 ml-4 flex ${
                  active
                    ? "font-bold text-navy-700 dark:text-white"
                    : "font-medium text-gray-600"
                }`}
              >
                {route.name}
              </p>
            </div>

            {hasChildren && (
              <MdKeyboardArrowRight
                className={`h-5 w-5 transform transition-transform duration-300 ease-in-out ${
                  openMap[route.name] ? "rotate-90" : "rotate-0"
                } ${
                  active ? "text-brand-500 dark:text-white" : "text-gray-600"
                }`}
              />
            )}
          </li>

          {active ? (
            <div className="absolute right-0 top-px h-9 w-1 rounded-lg bg-brand-500 dark:bg-brand-400" />
          ) : null}
        </div>
      );

      if (!hasChildren) {
        return (
          <Link key={index} to={"/" + route.path}>
            {ParentRow}
          </Link>
        );
      }

      return (
        <div key={index}>
          {ParentRow}
          {openMap[route.name] && (
            <ul className="mb-3">
              {route.children.map((child, cIdx) => {
                const childActive = activeRoute(child.path);
                if (child.hidden) return null;
                return (
                  <Link key={`${index}-${cIdx}`} to={"/" + child.path}>
                    <li className="my-3 flex cursor-pointer items-center pl-14 pr-8">
                      <span
                        className={`h-0.5 w-1.5 rounded-full ${
                          childActive
                            ? "bg-brand-500 dark:bg-white"
                            : "bg-gray-600"
                        }`}
                      />
                      <p
                        className={`ml-3 ${
                          childActive
                            ? "font-semibold text-navy-700 dark:text-white"
                            : "font-medium text-gray-600"
                        }`}
                      >
                        {child.name}
                      </p>
                    </li>
                  </Link>
                );
              })}
            </ul>
          )}
        </div>
      );
    });

  return createLinks(routes);
}

export default SidebarLinks;
